/**
 * Format an ISO-8601 expiry timestamp as a short countdown ("3d left",
 * "5h left", "expired"). Empty / unparseable input yields an em-dash.
 *
 * `now` is injectable so tests can pass a fixed clock instead of mocking Date.
 */
export function formatCountdown(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): string {
  if (!expiresAt) return '—';
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return '—';
  const ms = deadline - now;
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d left`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h left`;
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  return `${minutes}m left`;
}

/**
 * Verbose time remaining until an expiry timestamp: whole days when >= 24h,
 * whole hours when 1-24h, "<1 hour" under an hour, "expired" once past, and
 * null when there is no day limit.
 *
 * `now` is injectable so tests can pass a fixed clock instead of mocking Date.
 */
export function formatRemaining(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): string | null {
  if (!expiresAt) return null;
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return null;
  const ms = deadline - now;
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return '<1 hour';
}

/**
 * Compact badge form of {@link formatRemaining}: "Nd" / "Nh" / "<1h" / "exp",
 * or null when there is no day limit.
 */
export function formatRemainingShort(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): string | null {
  if (!expiresAt) return null;
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return null;
  const ms = deadline - now;
  if (ms <= 0) return 'exp';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / 3_600_000);
  return hours >= 1 ? `${hours}h` : '<1h';
}

/**
 * Whether a temp-account allowance is exhausted, and why. Time first (deadline
 * reached) → 'expired'; otherwise the server-sent `exhausted` flag (or a 0%
 * `percent_remaining`) → 'over_quota'. The demo user never receives raw token
 * counts, so this reads only the percentage view. A missing allowance returns
 * null. Drives the proactive expiry modal + the terminal/run-command lock from
 * the header allowance poll (#1054).
 *
 * `now` is injectable so tests can pass a fixed clock instead of mocking Date.
 */
export function isTempExhausted(
  temp:
    | {
        exhausted?: boolean | null;
        percent_remaining?: number | null;
        expires_at?: string | null;
      }
    | null
    | undefined,
  now: number = Date.now()
): 'expired' | 'over_quota' | null {
  if (!temp) return null;
  if (temp.expires_at) {
    const deadline = new Date(temp.expires_at).getTime();
    if (!Number.isNaN(deadline) && deadline <= now) return 'expired';
  }
  if (temp.exhausted || (typeof temp.percent_remaining === 'number' && temp.percent_remaining <= 0))
    return 'over_quota';
  return null;
}

/**
 * Percentage of a token quota consumed, clamped to 0-100. Returns null when
 * no limit is set (nothing to enforce, so no progress bar).
 */
export function quotaPct(
  used: number | null | undefined,
  limit: number | null | undefined
): number | null {
  if (limit == null || limit <= 0) return null;
  const ratio = ((used ?? 0) / limit) * 100;
  return Math.max(0, Math.min(100, Math.round(ratio)));
}

export type ExpiryUnit = 'minutes' | 'hours' | 'days';

/**
 * Translate an admin "expire after <value> <unit>" duration into the wire
 * fields the temp-account routes accept (#1054). Days map to `day_limit`
 * (clock anchored at first login / account creation); hours and minutes map to
 * `expire_in_minutes` (a deadline N minutes from now). The other field is set
 * null so the backend clears any prior value. Returns both null for a blank
 * value (= no time-based expiry), or null to signal an invalid (non-positive /
 * non-integer) entry the caller should reject.
 */
export function durationToWire(
  value: string,
  unit: ExpiryUnit
): { day_limit: number | null; expire_in_minutes: number | null } | null {
  const trimmed = value.trim();
  if (!trimmed) return { day_limit: null, expire_in_minutes: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n <= 0) return null;
  if (unit === 'days') return { day_limit: n, expire_in_minutes: null };
  if (unit === 'hours') return { day_limit: null, expire_in_minutes: n * 60 };
  return { day_limit: null, expire_in_minutes: n };
}
